import type { ConfusionMatrix as ConfusionMatrixData } from '../../lib/aggregates.ts';
import { OTHER_BUCKET } from '../../lib/aggregates.ts';

interface ConfusionMatrixProps {
  matrix: ConfusionMatrixData;
}

// 18×18 grid (9 disciplines + 8 specials + __other__). Predicted on rows,
// truth on columns. Diagonal cells get a green outline; off-diagonal cells
// scale background opacity by relative count so the largest mismatches stand
// out without an explicit legend.
export function ConfusionMatrix({ matrix }: ConfusionMatrixProps) {
  const max = computeMax(matrix);

  return (
    <div style={cardStyle}>
      <div style={headerRowStyle}>
        <div style={labelStyle}>CONFUSION MATRIX</div>
        <div style={subtleLabelStyle}>predicted ↓ · truth →</div>
      </div>

      {matrix.totalGraded === 0 ? (
        <div style={{ color: '#525870', fontSize: 13, marginTop: 12 }}>
          No graded pages yet.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={cornerStyle} />
                {matrix.axis.map((entry) => (
                  <th key={entry.value} style={colHeaderStyle} title={entry.label}>
                    <span style={rotatedLabelStyle}>{entry.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.axis.map((rowEntry) => (
                <tr key={rowEntry.value}>
                  <th style={rowHeaderStyle} title={rowEntry.label}>
                    {rowEntry.label}
                  </th>
                  {matrix.axis.map((colEntry) => {
                    const count = matrix.cells.get(rowEntry.value)?.get(colEntry.value) ?? 0;
                    const isDiag = rowEntry.value === colEntry.value;
                    return (
                      <td
                        key={colEntry.value}
                        style={cellStyle(count, max, isDiag)}
                        title={`${rowEntry.label} → ${colEntry.label}: ${count}`}
                      >
                        {count === 0 ? '' : count}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(matrix.otherPredictedRaw.length > 0 || matrix.otherTruthRaw.length > 0) && (
        <div style={otherSidebarStyle}>
          <div style={labelStyle}>{OTHER_BUCKET} raw values</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 8 }}>
            <RawList title="Predicted" values={matrix.otherPredictedRaw} />
            <RawList title="Truth" values={matrix.otherTruthRaw} />
          </div>
        </div>
      )}
    </div>
  );
}

function RawList({ title, values }: { title: string; values: readonly string[] }) {
  return (
    <div style={{ flex: 1, minWidth: 200 }}>
      <div style={{ ...subtleLabelStyle, marginBottom: 6 }}>{title}</div>
      {values.length === 0 ? (
        <div style={{ color: '#525870', fontSize: 12 }}>—</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {values.map((v) => (
            <span key={v} style={chipStyle}>
              {v}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function computeMax(matrix: ConfusionMatrixData): number {
  let max = 0;
  for (const row of matrix.cells.values()) {
    for (const count of row.values()) {
      if (count > max) max = count;
    }
  }
  return max;
}

function cellStyle(count: number, max: number, isDiagonal: boolean): React.CSSProperties {
  const intensity = max === 0 ? 0 : count / max;
  const baseColor = isDiagonal ? '0,232,122' : '255,107,122';
  const background = count === 0 ? 'transparent' : `rgba(${baseColor},${0.15 + intensity * 0.55})`;
  return {
    padding: '4px 6px',
    minWidth: 28,
    height: 28,
    textAlign: 'center',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 11,
    color: count === 0 ? '#222538' : '#030804',
    fontWeight: count > 0 ? 700 : 400,
    background,
    border: isDiagonal ? '1px solid rgba(0,232,122,0.5)' : '1px solid rgba(255,255,255,0.04)',
  };
}

const cardStyle: React.CSSProperties = {
  padding: 18,
  background: '#0D0F1C',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10,
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
};

const labelStyle: React.CSSProperties = {
  color: '#525870',
  fontSize: 10,
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
};

const subtleLabelStyle: React.CSSProperties = {
  color: '#525870',
  fontSize: 10,
  fontFamily: "'JetBrains Mono', monospace",
};

const tableStyle: React.CSSProperties = {
  borderCollapse: 'collapse',
  marginInline: 'auto',
};

const cornerStyle: React.CSSProperties = {
  width: 110,
  height: 90,
};

const colHeaderStyle: React.CSSProperties = {
  width: 28,
  height: 90,
  verticalAlign: 'bottom',
  padding: 0,
};

const rotatedLabelStyle: React.CSSProperties = {
  display: 'inline-block',
  transform: 'rotate(-60deg)',
  transformOrigin: 'left bottom',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 10,
  color: '#B8BDD6',
  whiteSpace: 'nowrap',
  paddingLeft: 4,
};

const rowHeaderStyle: React.CSSProperties = {
  textAlign: 'right',
  paddingRight: 8,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 10,
  color: '#B8BDD6',
  whiteSpace: 'nowrap',
  fontWeight: 500,
};

const otherSidebarStyle: React.CSSProperties = {
  marginTop: 14,
  paddingTop: 14,
  borderTop: '1px solid rgba(255,255,255,0.06)',
};

const chipStyle: React.CSSProperties = {
  background: '#1B1F35',
  color: '#B8BDD6',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 10,
  padding: '3px 8px',
  borderRadius: 4,
  border: '1px solid rgba(255,255,255,0.06)',
};
